/**
 * HttpEngineAdapter — REST + Socket.IO bridge to /api.
 *
 * Has its OWN apiFetch (its errors are raw text, not the JSON `message` the other services parse),
 * but takes its Authorization header from lib/apiClient so there is one answer to "how is a token
 * obtained". The comment here once claimed it used apiClient's helper; it did not, and the header
 * was missing entirely — every call 401'd the moment real auth was switched on.
 * Socket.IO events arrive on the ROOT namespace — `setupSocket(io)` registers the
 * `engine:subscribe` handler and `EngineRunner` emits `engine:event:<runId>` on the
 * root `io` (same namespace the compare/metadata stream uses). Connecting to a
 * `/deployments` namespace (which the server never registers) silently dropped all
 * live run progress; keep this aligned with HttpMetadataService.
 * Selected by VITE_ENGINE=http.
 */

import { authHeaders } from '../../lib/apiClient'
import type { IEngineAdapter } from './IEngineAdapter.interface'
import type {
  DeployRequest,
  DeploymentPlan,
  DeploymentRun,
  DeploymentRunSummary,
  EngineMeta,
  EngineEvent,
  EnvironmentStatus,
} from '../../types/engine.types'

// ── REST helper ───────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status}: ${body}`)
  }
  return res.json() as Promise<T>
}

// ── HttpEngineAdapter ─────────────────────────────────────────────────────────

export class HttpEngineAdapter implements IEngineAdapter {
  // Lazy Socket.IO import — avoids bundling socket.io-client when using mock.
  private _socket: unknown = null

  private async getSocket(): Promise<import('socket.io-client').Socket> {
    if (this._socket) return this._socket as import('socket.io-client').Socket
    const { connectSocket } = await import('../../lib/socket')
    // Root namespace (matches the server + the compare/metadata stream). NOT a
    // `/deployments` namespace — the server never registers one, so that dropped
    // every engine:event:<runId>.
    const socket = connectSocket({ url: API_BASE || '' })
    this._socket = socket
    return socket
  }

  // ── Meta ───────────────────────────────────────────────────────────────────

  getMeta(): Promise<EngineMeta> {
    return apiFetch<EngineMeta>('/api/meta')
  }

  // ── Runs ───────────────────────────────────────────────────────────────────

  listRuns(pipelineId?: string): Promise<DeploymentRunSummary[]> {
    const qs = pipelineId ? `?pipelineId=${encodeURIComponent(pipelineId)}` : ''
    return apiFetch<DeploymentRunSummary[]>(`/api/runs${qs}`)
  }

  getRun(id: string): Promise<DeploymentRun> {
    return apiFetch<DeploymentRun>(`/api/runs/${encodeURIComponent(id)}`)
  }

  startRun(req: DeployRequest): Promise<DeploymentRun> {
    return apiFetch<DeploymentRun>('/api/runs', {
      method: 'POST',
      body:   JSON.stringify(req),
    })
  }

  cancelRun(id: string): Promise<void> {
    return apiFetch<void>(`/api/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' })
  }

  approveGate(runId: string, gateId: string): Promise<void> {
    return apiFetch<void>(
      `/api/runs/${encodeURIComponent(runId)}/approve`,
      { method: 'POST', body: JSON.stringify({ gateId }) },
    )
  }

  retryEntity(runId: string, entityId: string): Promise<void> {
    return apiFetch<void>(
      `/api/runs/${encodeURIComponent(runId)}/entities/${encodeURIComponent(entityId)}/retry`,
      { method: 'POST' },
    )
  }

  rollback(runId: string, entityId?: string): Promise<void> {
    return apiFetch<void>(
      `/api/runs/${encodeURIComponent(runId)}/rollback`,
      {
        method: 'POST',
        body:   JSON.stringify(entityId !== undefined ? { entityId } : {}),
      },
    )
  }

  // ── Plan ───────────────────────────────────────────────────────────────────

  getPlan(req: DeployRequest): Promise<DeploymentPlan> {
    return apiFetch<DeploymentPlan>('/api/runs/plan', {
      method: 'POST',
      body:   JSON.stringify(req),
    })
  }

  // ── Environment status ─────────────────────────────────────────────────────

  getEnvironmentStatuses(pipelineId: string): Promise<EnvironmentStatus[]> {
    return apiFetch<EnvironmentStatus[]>(
      `/api/environments/status?pipelineId=${encodeURIComponent(pipelineId)}`,
    )
  }

  // ── Subscribe ──────────────────────────────────────────────────────────────

  subscribe(runId: string, onEvent: (event: EngineEvent) => void): () => void {
    let socket: import('socket.io-client').Socket | null = null
    let cancelled = false

    void this.getSocket().then((s) => {
      if (cancelled) return
      socket = s
      s.emit('engine:subscribe', runId)
      s.on(`engine:event:${runId}`, onEvent)
    })

    return () => {
      cancelled = true
      if (socket) {
        socket.emit('engine:unsubscribe', runId)
        socket.off(`engine:event:${runId}`, onEvent)
      }
    }
  }

  subscribeEnvironments(pipelineId: string, onEvent: (event: EngineEvent) => void): () => void {
    let socket: import('socket.io-client').Socket | null = null
    let cancelled = false

    void this.getSocket().then((s) => {
      if (cancelled) return
      socket = s
      s.emit('engine:subscribe:env', pipelineId)
      s.on(`engine:env:${pipelineId}`, onEvent)
    })

    return () => {
      cancelled = true
      if (socket) {
        socket.emit('engine:unsubscribe:env', pipelineId)
        socket.off(`engine:env:${pipelineId}`, onEvent)
      }
    }
  }
}
